import React, { useEffect, useRef, useState } from "react";
import validator from "validator";
import { parseISO, differenceInDays } from "date-fns";
import {
  PersonFormState,
  NameState,
  EmailState,
  PhonesState,
  AddressesState,
  DegreesState,
  JobState,
} from "../api/Reports";
import { ErrorTypes, PersonFormStateError } from "./PersonFormErrorState";
import {
  cleanedProfileUrl,
  collectProfileUrls,
  getProfileUrlValidation,
  PROFILE_URL_ERROR,
} from "../utils/personFieldLimits";

type Props = {
  formState: PersonFormState;
  handleError: (value: PersonFormStateError) => void;
  showErrors: boolean;
};

interface ErrorMessage {
  message?: string;
}

function hasValidAttributesCharacters(obj: any | any[]): boolean {
  const charObjValidator = (element: any) => {
    const keys = Object.keys(element);
    for (let index = 0; index < keys.length; index++) {
      const prop = element[keys[index]];
      if (
        prop !== undefined &&
        prop !== null &&
        !validator.isEmpty(prop) &&
        !validator.isAlphanumeric(prop, "en-US", {
          ignore: "s/',&.-@ÁÉÍÓÚáéíóúÄËÏÖÜäëïöü/]}[{~`!^()@#$%\"?¿|*",
        })
      ) {
        return false;
      }
    }
    return true;
  };

  if (Array.isArray(obj)) {
    for (let index = 0; index < obj.length; index++) {
      const pivot = obj[index];
      if (!charObjValidator(pivot)) {
        return false;
      }
    }
    return true;
  }

  return charObjValidator(obj);
}

function validateNames(
  names: NameState[],
  errorState: PersonFormStateError
): ErrorMessage {
  let first_name = [];
  let last_name = [];
  if (names.length === 0) {
    first_name[0] = {
      errorType: ErrorTypes.isRequired,
      message: "Please add First Name",
    };
    last_name[0] = {
      errorType: ErrorTypes.isRequired,
      message: "Please add Last Name",
    };
    errorState.names = { first_name: first_name, last_name: last_name };
    return { message: "Please have at least one name" };
  }

  let hasError = false;
  for (let index = 0; index < names.length; index++) {
    const name = names[index];
    hasError = false;
    if (
      validator.isEmpty(name.first_name) ||
      (!validator.isAscii(name.first_name) &&
        !validator.isAlpha(name.first_name, "en-US", {
          ignore: "ÁÉÍÓÚÑáéíóúñÄËÏÖÜäëïöüČčŠšŘřŽžĎďĽľŇňŤťŁȘçØø",
        }))
    ) {
      first_name[index] = {
        errorType: ErrorTypes.isRequired,
        message: "Please add First Name with letters only",
      };
      hasError = true;
    }
    if (
      validator.isEmpty(name.last_name) ||
      (!validator.isAscii(name.last_name) &&
        !validator.isAlpha(name.last_name, "en-US", {
          ignore: "ÁÉÍÓÚÑáéíóúñÄËÏÖÜäëïöüČčŠšŘřŽžĎďĽľŇňŤťŁȘçØø",
        }))
    ) {
      last_name[index] = {
        errorType: ErrorTypes.isRequired,
        message: "Please add Last Name with letters only",
      };
      hasError = true;
    }

    const combinedFullName = [name.first_name, name.middle_name, name.last_name]
      .map((part) => (part ?? "").trim())
      .filter((part) => part.length > 0)
      .join(" ");
    if (combinedFullName.length > 255) {
      first_name[index] = {
        errorType: ErrorTypes.isNotValid,
        message: "Combined full name must not exceed 255 characters",
      };
      hasError = true;
    }

    if (hasError) {
      errorState.names = { first_name: first_name, last_name: last_name };
      return {
        message:
          combinedFullName.length > 255
            ? "Combined full name must not exceed 255 characters"
            : "Please add First Name and/or Last Name with letters only",
      };
    }
  }

  return {};
}

function validateEmails(
  emails: EmailState[],
  errorState: PersonFormStateError
): ErrorMessage {
  if (emails.length === 0) {
    (errorState.emails = []) &&
      (errorState.emails[0] = {
        errorType: ErrorTypes.isRequired,
        message: "Please have at least one email",
      });
    return { message: "Please have at least one email" };
  }

  for (let index = 0; index < emails.length; index++) {
    const email = emails[index];
    if (
      !validator.isEmail(email.address) &&
      (errorState.emails || (errorState.emails = []))
    ) {
      errorState.emails[index] = {
        errorType: ErrorTypes.isNotValid,
        message: "Please have a valid email",
      };
      return { message: "Please have a valid email" };
    }
  }
  return {};
}

function validatePhones(
  phones: PhonesState[],
  errorState: PersonFormStateError,
  errors: String[]
) {
  for (let index = 0; index < phones.length; index++) {
    const phone = phones[index];
    if (
      phone.raw_number.length === 0 &&
      (errorState.phones || (errorState.phones = []))
    ) {
      errorState.phones[index] = {
        errorType: ErrorTypes.isNotValid,
        message: "Please have a valid phone number",
      };

      errors.push("Please have a valid phone number");
    }
  }
}

function validateBirthdate(
  birthdate: string,
  errorState: PersonFormStateError
): ErrorMessage {
  if (!validator.isEmpty(birthdate) && validator.isDate(birthdate)) {
    let isodate = parseISO(birthdate);
    if (!isodate) {
      errorState.birth_date = {
        errorType: ErrorTypes.isNotValid,
        message: "please have valid birth date",
      };
      return { message: "please have valid birth date" };
    }
  }

  return {};
}

function validateProfiles(
  profiles: string[],
  errorState: PersonFormStateError
): ErrorMessage {
  let error = false;
  let errorMessage = "Please enter valid Profile Url";

  if (profiles.length > 1) {
    const { duplicateKeys } = collectProfileUrls(profiles);
    const keys = profiles.map((p) => cleanedProfileUrl(p));
    keys.forEach((key: string) => {
      if (
        key.length > 0 &&
        duplicateKeys.has(key) &&
        (errorState.profiles || (errorState.profiles = []))
      ) {
        const foundIdx = keys.lastIndexOf(key);
        errorState.profiles[foundIdx] = {
          errorType: ErrorTypes.isNotValid,
          message: PROFILE_URL_ERROR.duplicate,
        };
        error = true;
        errorMessage = PROFILE_URL_ERROR.duplicate;
      }
    });

    if (error) return { message: errorMessage };
  }

  profiles.forEach((profile, idx) => {
    const result = getProfileUrlValidation(profile, { allowEmpty: true });
    if (result.ok) {
      profiles[idx] = result.normalized;
      return;
    }
    profiles[idx] = profile.trim();
    if (errorState.profiles || (errorState.profiles = [])) {
      errorMessage = result.message;
      errorState.profiles[idx] = {
        errorType: ErrorTypes.isNotValid,
        message: errorMessage,
      };
      error = true;
    }
  });
  return error ? { message: errorMessage } : {};
}

function validateMultiple(
  list: AddressesState[] | DegreesState[] | JobState[] | string[],
  errorState: PersonFormStateError,
  errors: String[],
  type: string
) {
  for (let index = 0; index < list.length; index++) {
    if (type === "degrees") {
      validateStartAndEndDates({
        startDate: (list[index] as DegreesState)?.start_date,
        endDate: (list[index] as DegreesState)?.end_date,
        errorState,
        errors,
        type,
      });
    }

    if (type === "jobs") {
      validateStartAndEndDates({
        startDate: (list[index] as JobState)?.start_date,
        endDate: (list[index] as JobState)?.end_date,
        errorState,
        errors,
        type,
      });
      let state = list[index] as JobState;
      if (state.title.length === 0) {
        if (!errorState.jobs) {
          errorState.jobs = { title: [], employer: [] };
        }
        errorState.jobs!.title![index] = {
          errorType: ErrorTypes.isRequired,
          message: "Please add Job Title",
        };
        errors.push("Please add Job Title");
      }

      if (state.employer.length === 0) {
        if (!errorState.jobs) {
          errorState.jobs = { title: [], employer: [] };
        }
        errorState.jobs!.employer![index] = {
          errorType: ErrorTypes.isRequired,
          message: "Please add Job Company Name",
        };
        errors.push("Please add Job Company Name");
      }
    }
  }

  if (!hasValidAttributesCharacters(list)) {
    errors.push(`Invalid characters`);
  }
}

function validateStartAndEndDates({
  startDate,
  endDate,
  errorState,
  errors,
  type,
}: {
  startDate?: string;
  endDate?: string;
  errorState?: PersonFormStateError;
  errors?: String[];
  type?: String;
}) {
  const hasStart = startDate && !validator.isEmpty(startDate);
  const hasEnd = endDate && !validator.isEmpty(endDate);
  let start: Date | undefined = undefined;
  let end: Date | undefined = undefined;
  if (hasStart && validator.isDate(startDate)) {
    start = parseISO(startDate);
    if (!start) {
      errorState &&
        type === "degrees" &&
        (errorState.degrees = {
          start_date: [
            {
              errorType: ErrorTypes.isNotValid,
              message:
                "Invalid Start Date, please make sure they have the right format",
            },
          ],
        });
      errorState &&
        type === "jobs" &&
        (errorState.jobs = {
          start_date: [
            {
              errorType: ErrorTypes.isNotValid,
              message:
                "Invalid Start Date, please make sure they have the right format",
            },
          ],
        });
    }
  }

  if (hasEnd && validator.isDate(endDate)) {
    end = parseISO(endDate);
    if (!end) {
      errorState &&
        type === "degrees" &&
        (errorState.degrees = {
          ...errorState.degrees,
          end_date: [
            {
              errorType: ErrorTypes.isNotValid,
              message:
                "Invalid End Date, please make sure they have the right format",
            },
          ],
        });
      errorState &&
        type === "jobs" &&
        (errorState.jobs = {
          ...errorState.jobs,
          end_date: [
            {
              errorType: ErrorTypes.isNotValid,
              message:
                "Invalid End Date, please make sure they have the right format",
            },
          ],
        });
    }
  }

  if (hasStart && hasEnd) {
    start = parseISO(startDate);
    end = parseISO(endDate);
    if (!start || !end) {
      return {};
    }
    if (start > new Date() || end > new Date()) {
      errors && errors.push("Does not support future dates");
      return {
        message: "Does not support future dates",
      };
    }
    const difference = differenceInDays(start, end);
    if (difference > 0) {
      errorState &&
        type === "degrees" &&
        (errorState.degrees = {
          start_date: [
            {
              errorType: ErrorTypes.isNotValid,
              message: "Please enter a start date that is before the end date.",
            },
          ],
          end_date: [
            {
              errorType: ErrorTypes.isNotValid,
              message: "Please enter a start date that is before the end date.",
            },
          ],
        });
      errorState &&
        type === "jobs" &&
        (errorState.jobs = {
          title: [],
          employer: [],
          start_date: [
            {
              errorType: ErrorTypes.isNotValid,
              message: "Please enter a start date that is before the end date.",
            },
          ],
          end_date: [
            {
              errorType: ErrorTypes.isNotValid,
              message: "Please enter a start date that is before the end date.",
            },
          ],
        });
      errors &&
        errors.push("Please enter a start date that is before the end date.");
      return {
        message: "Please enter a start date that is before the end date.",
      };
    }
    return {};
  }
}

const usePrevious = (state?: PersonFormState) => {
  const ref = useRef<PersonFormState | undefined>();
  useEffect(() => {
    ref.current = state;
  }, [state]);
  return ref.current;
};

const useTraceableState = (): [
  PersonFormState | undefined,
  PersonFormState | undefined,
  any
] => {
  const [value, setValue] = useState();
  const prevState = usePrevious(value);
  return [prevState, value, setValue];
};

const PersonFormErrorSummary = ({
  formState,
  handleError,
  showErrors,
}: Props) => {
  const errors: string[] = [];
  const errorsState: PersonFormStateError = {};
  const [prevState, , setValue] = useTraceableState();
  let isSameState = false;
  useEffect(() => {
    setValue(formState);
  }, [formState, setValue, handleError]);

  if (formState === prevState) isSameState = true;

  if (!formState.company_uuid) {
    errors.push(`Please select a company`);
    errorsState.company = {
      errorType: ErrorTypes.isRequired,
      message: "Please select a company",
    };
  }
  const names = validateNames(formState.names, errorsState);
  if (names.message) errors.push(names.message);

  if (!formState.blueprint_uuid) {
    errors.push(`Please select a check type`);
    errorsState.blueprint_uuid = {
      errorType: ErrorTypes.isRequired,
      message: "Please select a check type",
    };
  }

  const email = validateEmails(formState.emails, errorsState);
  if (email.message) {
    errors.push(email.message);
  }

  validatePhones(formState.phones, errorsState, errors);

  const birthdate = validateBirthdate(formState.birth_date, errorsState);
  if (birthdate.message) errors.push(birthdate.message);

  const profiles = validateProfiles(formState.profiles, errorsState);
  if (profiles.message) {
    errors.push(profiles.message);
  }

  validateMultiple(formState.addresses, errorsState, errors, "address");
  validateMultiple(formState.degrees, errorsState, errors, "degrees");
  validateMultiple(formState.jobs, errorsState, errors, "jobs");

  if (!isSameState) handleError(errorsState);

  return (
    <>
      {showErrors && errors.length > 0 && (
        <div className="p-4 my-4 bg-red-300 text-red-900 rounded-lg">
          <ul>
            {errors.map((error, i) => (
              <li key={i}>- {error}</li>
            ))}
          </ul>
        </div>
      )}
    </>
  );
};

export default PersonFormErrorSummary;
